import {ArrowBack, ArrowForwardIos, Menu} from '@mui/icons-material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {Box, IconButton, Typography} from '@mui/material';
import {FC, useMemo} from 'react';
import {
    breadcrumbsSx,
    breadcrumbsWrapperSx,
    buttonWrapperSx,
    contextLineSx,
    currentPageSx,
    headerWrapperSx,
    separateIconSx,
    textSx,
    titleWrapperSx,
} from './AMPNavbar.sx';
import useBreadcrumbs from 'use-react-router-breadcrumbs';
import {Link, useLocation} from 'react-router-dom';
import {FormattedMessage} from 'react-intl';
import {AMPNavbarType} from './AMPNavbar.types';
import {rememberedListQuery} from 'src/hooks/useListQueryState';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';

export const AMPNavbar: FC<AMPNavbarType> = ({handleOpenDrawer}) => {
    const location = useLocation();
    const breadcrumbs = useBreadcrumbs().slice(2);
    const currentPage = useMemo(
        () => {
            // Special case for dynamic pricing page
            const pricingMatch = location.pathname.match(/^\/app\/locations\/(\d+)\/prices\/\d+$/);
            if (pricingMatch) {
                return 'dynamicPriceConfiguration';
            }

            // "businesses" loses only its final letter to the derivation below, which asks for a
            // key nobody would think to write. Named here instead.
            if (location.pathname.match(/^\/app\/businesses\/\d+$/)) {
                return 'businessDetails';
            }

            // Special case for charging-sessions detail page
            if (location.pathname.match(/^\/app\/charging-sessions\/\d+$/)) {
                return 'charging-session-details';
            }

            // The three single pages under access-control share their first crumb, so the crumb alone
            // cannot say which one you are on - and the derivation below cannot either, because it
            // stops at the hyphen and produced the raw slug "access-control" as a title.
            const accessControl = location.pathname.match(/^\/app\/access-control\/(roles|users|permissions)\//);
            if (accessControl) {
                switch (accessControl[1]) {
                    case 'roles':
                        return 'accessControlRoleDetails';
                    case 'users':
                        return 'accessControlUserDetails';
                    default:
                        return 'accessControlPermissionDetails';
                }
            }

            return breadcrumbs.length &&
                breadcrumbs[0].key.replace(/s$/, 'Details').replace(/(\/app\/)(\w+)/, '$2');
        },
        [breadcrumbs, location.pathname]
    );

    // Back out to locations rather than the team list for anyone who cannot open the team list.
    // The role list this replaces named five roles and would have sent a custom role to a page it is
    // not allowed to load.
    const shouldRedirectToLocations = !useCan('users.search');
    const isOnUserPage = location.pathname.includes('/app/users/') || location.pathname.includes('/app/technical-team/');

    // The breadcrumb bar belongs to the back-office shell, so it asks the same question AMPDrawer asks
    // when choosing that shell. It used to ask `ability.can('manage', 'all')`, which no permission
    // grants any more - the bar had disappeared for everyone, including the business owner.
    const {isBackOffice} = usePersona();

    /**
     * A list route, with the query it was last looked at with.
     *
     * This crumb is a link to the bare route, so leaving a detail page by it used to land on an
     * unfiltered list on page one - while the browser's own Back, which carries the query in its
     * history entry, came back to exactly what you left. The two now agree.
     */
    const toList = (url: string) => {
        const query = rememberedListQuery(url);
        return query ? `${url}?${query}` : url;
    };

    // Determine the back navigation URL and text
    const getBackNavigation = () => {
        // /app has no page segment, so there is no crumb and nothing to go back to. This used to
        // dereference breadcrumbs[0] regardless and crashed the whole shell on that one URL.
        if (!breadcrumbs.length) {
            return null;
        }
        // Special case for dynamic pricing page - go back to specific location
        const pricingMatch = location.pathname.match(/^\/app\/locations\/(\d+)\/prices\/\d+$/);
        if (pricingMatch) {
            return {
                url: `/app/locations/${pricingMatch[1]}`,
                text: 'locationDetails'
            };
        }

        if (shouldRedirectToLocations && isOnUserPage) {
            return {
                url: toList(AppRouteSpace([AppRouteEnum.app, AppRouteEnum.locations])),
                text: 'locations'
            };
        }

        // Special case for charging-sessions breadcrumb — respect source path via state.backTo
        if (breadcrumbs[0]?.key === '/app/charging-sessions') {
            const backTo = (location.state as {backTo?: string} | null)?.backTo;
            if (backTo && backTo.includes('/reports/charging-sessions')) {
                return {
                    url: backTo,
                    text: 'chargingSessions'
                };
            }
            return {
                url: toList(breadcrumbs[0].key),
                text: 'chargingSessions'
            };
        }

        return {
            url: toList(breadcrumbs[0].key),
            text: breadcrumbs[0].key.replace(/(\/app\/)(\w+)/, '$2')
        };
    };

    const backNavigation = getBackNavigation();

    return (
        <Box sx={headerWrapperSx}>
            {breadcrumbs.length > 1 && isBackOffice && backNavigation && (
                <Box sx={breadcrumbsWrapperSx}>
                    <Box sx={breadcrumbsSx}>
                        <Link
                            to={backNavigation.url}
                            state={location.state}
                        >
                            <ArrowBack/>
                            <Typography
                                variant="subtitle1"
                                sx={textSx}
                            >
                                <FormattedMessage id={backNavigation.text}/>
                            </Typography>
                        </Link>
                    </Box>
                    <ArrowForwardIos sx={separateIconSx}/>
                    {/* The page's name, and under it the one it was opened from. On a desktop the
                        trail to the left already says where that is, so the second line appears only
                        below `md` - the same message, in the place a phone has room for it. */}
                    <Box sx={titleWrapperSx}>
                        <Typography
                            variant="subtitle1"
                            sx={[textSx, currentPageSx]}
                        >
                            {currentPage && (
                                <FormattedMessage
                                    id={currentPage === 'technical-team' ? 'memberProfile' : currentPage}
                                />
                            )}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={contextLineSx}
                        >
                            <FormattedMessage id={backNavigation.text}/>
                        </Typography>
                    </Box>
                </Box>
            )}
            <Box sx={buttonWrapperSx}>
                <IconButton
                    aria-label="open drawer"
                    onClick={handleOpenDrawer}
                    edge="start"
                >
                    <Menu/>
                </IconButton>
            </Box>
        </Box>
    );
};
